import includes from 'lodash/includes';
import reject from 'lodash/reject';
import moment from 'moment-timezone';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Cookies } from '@socrata/platform/cookies';
import Environment from '../../StorytellerEnvironment';
import { exceptionNotifier } from '../../services/ExceptionNotifier';

export const DOWNTIME_MOMENT_FORMAT_STRING = 'YYYY-MM-DD[t]HH:mmZ';

const ACKNOWLEDGEMENTS_COOKIE = 'maintenance_ack';

export interface Downtime {
  message_start: string; // timestamp of when the maintenance banner will start showing
  message_end: string; // timestamp of when the maintenance banner will stop showing
  downtime_start: string; // timestamp that will render on the maintenance banner as the start of the downtime
  downtime_end: string; // timestamp that will render on the maintenance banner as the end of the downtime
}

/**
 * Reads the acknowledged downtimes as unix timestamps. Frontend writes the same
 * cookie, so an absent or unparseable value reads as no acknowledgements rather
 * than throwing.
 */
function readAcknowledgements(): number[] {
  const value = Cookies.get(ACKNOWLEDGEMENTS_COOKIE);
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAcknowledgements(acknowledgements: number[]) {
  Cookies.set(ACKNOWLEDGEMENTS_COOKIE, JSON.stringify(acknowledgements));
}

function downtimeToAck(message_start: string) {
  return moment(message_start, DOWNTIME_MOMENT_FORMAT_STRING).unix();
}

function isDowntimeOpen({ message_start, message_end }: { message_start: string; message_end: string }) {
  const messageStart = moment(message_start, DOWNTIME_MOMENT_FORMAT_STRING);
  const messageEnd = moment(message_end, DOWNTIME_MOMENT_FORMAT_STRING);
  return moment().isBetween(messageStart, messageEnd);
}

function acknowledge(downtime: Downtime): number[] {
  if (!downtime) {
    exceptionNotifier.notify(new Error('Did not specify a downtime to acknowledge'));
    return readAcknowledgements();
  }

  const acknowledgements = readAcknowledgements().concat(downtimeToAck(downtime.message_start));
  writeAcknowledgements(acknowledgements);

  return acknowledgements;
}

const downtimeSlice = createSlice({
  name: 'downtimeSlice',
  initialState: {
    cookies: readAcknowledgements(),
  },
  reducers: {
    resetCookie() {
      writeAcknowledgements([]);

      return { cookies: [] };
    },
    downtimeAcknowledge(state, action: PayloadAction<Downtime>) {
      return { cookies: acknowledge(action.payload) };
    },
  },
});

export const getUnacknowledgedDowntimes = () => {
  const knownDowntimes = Environment.DOWNTIMES;
  const acknowledgements = readAcknowledgements();
  return reject(knownDowntimes, (downtime: Downtime) => {
    return includes(acknowledgements, downtimeToAck(downtime.message_start)) || !isDowntimeOpen(downtime);
  });
};

export const downTimeSliceReducer = downtimeSlice.reducer;
export type DownTimeSliceState = ReturnType<typeof downTimeSliceReducer>;

export const { resetCookie, downtimeAcknowledge } = downtimeSlice.actions;
